/**
 * Precast Column / Beam placement tools (BIM: Precast > Structure)
 *
 * Precast columns: click on the work plane to drop a factory-cast column at
 * the active level; size and storey height come from the Configuration.
 * Precast beams: two clicks define the member axis at the current elevation;
 * the section follows the tool defaults. Both tools stay active for repeated
 * placement and chain BIM-style (Esc ends, mark numbers auto-increment).
 */
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { BaseTool } from '../BaseTool';
import { PrecastColumnElement, PrecastBeamElement } from '../../core/types';
import { createPrecastColumn, createPrecastBeam } from '../../utils/precastGeometry';
import { getPrecastConfig } from '../../state/usePrecastConfigStore';
import i18n from '../../i18n';

/** Shared mark counter + section defaults for the two member tools. */
abstract class PrecastMemberToolBase extends BaseTool {
    protected counter = 0;

    protected sectionWidthMm(): number { return 400; }
    protected sectionDepthMm(): number { return 400; }

    protected columnHeightM(): number {
        return getPrecastConfig().maxPanelHeightMm / 1000;
    }
}

/** Precast Column placement (one click = base point at the active level). */
export class PrecastColumnTool extends PrecastMemberToolBase {
    id = 'precast_column';
    name = 'Precast Column';
    cursor = 'crosshair';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const point = this.getIntersection(event, 0);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        this.counter += 1;
        const mark = `PC-${this.counter}`;
        const column: PrecastColumnElement = createPrecastColumn(
            [snapped.x, snapped.y, 0],
            {
                widthMm: this.sectionWidthMm(),
                depthMm: this.sectionDepthMm(),
                heightMm: this.columnHeightM() * 1000,
                mark,
            },
        );
        this.context.bimStore.addElement(column);
        this.notify(i18n.t('tools.notify.precastColumnPlaced', { mark: mark }), 'success');
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event, 0);
        if (!point) return;
        this.context.appStore.setToolPreview({
            type: 'column',
            position: new THREE.Vector3(point.x, point.y, 0),
            height: this.columnHeightM(),
            width: this.sectionWidthMm() / 1000,
            depth: this.sectionDepthMm() / 1000,
            color: '#c9a227',
        });
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.context?.appStore.setToolPreview(null);
        }
        super.onKeyDown(event);
    }

    deactivate(): void {
        this.context?.appStore.setToolPreview(null);
        super.deactivate();
    }
}

/** Precast Beam placement (two clicks = member axis at the work plane). */
export class PrecastBeamTool extends PrecastMemberToolBase {
    id = 'precast_beam';
    name = 'Precast Beam';
    cursor = 'crosshair';

    private start: THREE.Vector3 | null = null;

    protected override sectionWidthMm(): number { return 300; }
    protected override sectionDepthMm(): number { return 600; }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const point = this.getIntersection(event, 0);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        if (!this.start) {
            this.start = new THREE.Vector3(snapped.x, snapped.y, this.sectionDepthMm() / 1000);
            return;
        }
        if (Math.hypot(snapped.x - this.start.x, snapped.y - this.start.y) < 0.1) {
            this.notify(i18n.t('tools.notify.precastBeamEndPointTooClose'), 'warning');
            return;
        }
        this.counter += 1;
        const mark = `PB-${this.counter}`;
        const beam: PrecastBeamElement = createPrecastBeam(
            [this.start.x, this.start.y, this.start.z],
            [snapped.x, snapped.y, this.start.z],
            { widthMm: this.sectionWidthMm(), depthMm: this.sectionDepthMm(), mark },
        );
        this.context.bimStore.addElement(beam);
        this.notify(i18n.t('tools.notify.precastBeamPlaced', { mark: mark }), 'success');
        this.start = null;
        this.context.appStore.setToolPreview(null);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.start) return;
        const point = this.getIntersection(event, 0);
        if (!point) return;
        this.context.appStore.setToolPreview({
            type: 'line',
            start: this.start,
            end: new THREE.Vector3(point.x, point.y, this.start.z),
            color: '#c9a227',
        });
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.start = null;
            this.context?.appStore.setToolPreview(null);
        }
        super.onKeyDown(event);
    }

    deactivate(): void {
        this.start = null;
        this.context?.appStore.setToolPreview(null);
        super.deactivate();
    }
}
